<script lang="ts">
export default {
  name: "PatientDetail",
};
</script>

<script setup lang="ts">
import { usePatient } from "@/composables/models/patient/usePatient";
import { definePage } from "unplugin-vue-router/runtime";
import { computed, ref } from "vue";
import { onBeforeRouteUpdate, useRoute } from "vue-router";
import { usePageTitle } from "@/composables/shared/usePageTitle";
import { patientSelectable } from "@/api/models/patient/patient";
import { useAbility } from "@casl/vue";
import ChatPatientLayout from "@/components/models/patient/ChatPatientLayout.vue";
import EntityCardSkeleton from "@/components/models/message/skeletons/EntityCardSkeleton.vue";
import ChatMessagesSkeleton from "@/components/models/message/skeletons/ChatMessagesSkeleton.vue";

definePage({
  meta: {
    title: "generic.action.viewEntity",
    titleParams: { entity: "generic.entity.patient" },
    layout: "rooms",
    mobileBottomChromeInset: true,
    privilegeScope: "section",
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.READ, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const route = useRoute();
// `route.params.id` is a single global reactive object that also mutates while navigating AWAY to
// another route (e.g. an instruction edit page whose `:id` is an instruction id). Deriving the query
// param straight from it makes this patient query "follow" the route onto non-patient ids and refetch
// the wrong id. Snapshot the id at mount and only update it for in-place param changes on THIS route.
const patientId = ref((route.params as { id: string }).id);
onBeforeRouteUpdate((to) => {
  patientId.value = (to.params as { id: string }).id;
});

const query = usePatient(
  {
    id: patientId,
    enabled: computed(() => !!patientId.value),
  },
  { redirectOnNotFound: true, accessCheck: (data) => !!data.is_patient }
);

const { data: patient, isLoading } = query;

usePageTitle({
  entityRef: patient,
  selectable: patientSelectable,
});
</script>

<template>
  <chat-patient-layout
    v-if="patient && !isLoading"
    :key="patientId"
    :query="query"
  />
  <div
    v-else
    class="tw:flex tw:flex-1 tw:h-full tw:overflow-hidden tw:bg-background tw:rounded-tl-xl"
  >
    <div class="tw:hidden tw:md:block tw:w-[30%] tw:h-full tw:flex-shrink-0 tw:!my-1">
      <entity-card-skeleton />
    </div>
    <div class="tw:flex-1 tw:h-full tw:min-w-0 tw:md:!mt-1">
      <chat-messages-skeleton />
    </div>
  </div>
</template>
